import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { insightsApi, type FilterSpec } from '../../api/insights';

interface NlSearchBoxProps {
  onSpec: (spec: FilterSpec | null, rawText: string) => void;
}

/**
 * Natural-language jump/filter box — the only LLM touchpoint in the drill
 * flow. One Mistral call turns e.g. "skolingi daugiau nei 1000" into a
 * FilterSpec applied client-side to the deterministic views. Any failure
 * degrades to plain text search (spec = null); the UI never blocks on AI.
 */
export default function NlSearchBox({ onSpec }: NlSearchBoxProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    setError('');
    const current = text;
    const result = await insightsApi.parseQuery(current);
    if (result.fallback) setError(t('analytics.nl.fallback'));
    onSpec(result.spec, current);
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="flex-1 min-w-[240px] max-w-xl">
      <div className="flex gap-2">
        <input
          type="search"
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={t('analytics.nl.placeholder')}
          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
        />
        <button
          type="submit"
          disabled={busy || !text.trim()}
          className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-40 transition-colors"
        >
          {busy ? t('analytics.nl.thinking') : t('analytics.nl.go')}
        </button>
      </div>
      {error && <p className="mt-1 text-xs text-amber-600">{error}</p>}
    </form>
  );
}
